/**
 * categoryChipUtils.js - Canonical category parsing and normalization utilities
 *
 * Single source of truth for parsing category strings into parent/leaf components
 * and normalizing keys for registry lookups.
 */

/**
 * Parse a raw category string into its component parts
 * Handles formats: "Parent > Child", "Category with - hyphen", "Category", null/undefined
 *
 * IMPORTANT: Only " > " is treated as a parent/child delimiter.
 * Hyphens within category names (like "Travel - Airfare") are preserved.
 *
 * @param {string} rawCategory - The raw category string
 * @returns {{ parent: string|null, leaf: string, raw: string }}
 */
export function getCategoryParts(rawCategory) {
  if (!rawCategory || typeof rawCategory !== 'string') {
    return { parent: null, leaf: 'Uncategorized', raw: '' };
  }

  const raw = rawCategory.trim();
  if (!raw) {
    return { parent: null, leaf: 'Uncategorized', raw: '' };
  }

  // Only split by " > " - this is the canonical parent/child delimiter
  // Hyphens in category names (like "Travel - Airfare") are preserved
  if (raw.includes(' > ')) {
    const parts = raw.split(' > ').map(p => p.trim()).filter(Boolean);

    if (parts.length === 0) {
      return { parent: null, leaf: 'Uncategorized', raw };
    }

    if (parts.length === 1) {
      return { parent: null, leaf: parts[0], raw };
    }

    // Last part is the leaf, first part is the parent
    const leaf = parts[parts.length - 1];
    const parent = parts[0];

    return { parent, leaf, raw };
  }

  // No " > " delimiter - the entire string is the leaf
  return { parent: null, leaf: raw, raw };
}

/**
 * Normalize a category label for registry lookup
 * - Trims whitespace
 * - Collapses multiple spaces
 * - Converts to lowercase
 * - Preserves symbols like "+", "&", "/", "-"
 *
 * @param {string} label - The category label to normalize
 * @returns {string} Normalized key for registry lookup
 */
export function normalizeKey(label) {
  if (!label || typeof label !== 'string') {
    return '';
  }

  return label
    .trim()
    .replace(/\s+/g, ' ')  // Collapse multiple spaces
    .toLowerCase();
}

/**
 * List of categories that belong to the Excluded parent group
 * These always render with gray tint regardless of their leaf name
 */
export const EXCLUDED_LEAVES = [
  'transfers',
  'cc payments',
  'refunds'
];

/**
 * Check if a category should be treated as Excluded
 * @param {string} leaf - The leaf category name
 * @param {string|null} parent - The parent category name
 * @returns {boolean}
 */
export function isExcludedCategory(leaf, parent) {
  if (!leaf) return false;

  const normalizedLeaf = normalizeKey(leaf);
  const normalizedParent = parent ? normalizeKey(parent) : null;

  // Check if parent is "Excluded"
  if (normalizedParent === 'excluded') {
    return true;
  }

  // Check if leaf is in excluded list
  return EXCLUDED_LEAVES.includes(normalizedLeaf);
}

/**
 * Check if a category should be treated as Uncategorized
 * @param {string} leaf - The leaf category name
 * @param {string} raw - The raw category string
 * @returns {boolean}
 */
export function isUncategorizedCategory(leaf, raw) {
  if (!raw || !leaf) return true;

  const normalizedLeaf = normalizeKey(leaf);
  return normalizedLeaf === 'uncategorized' || normalizedLeaf === '';
}

/**
 * Complete list of all leaf categories in our source of truth
 * Used for validation and testing
 */
export const ALL_LEAF_CATEGORIES = [
  // Fixed
  'Rent/Mortgage',
  'Car Payment',
  'Car Insurance',
  'Phone',
  'Internet',
  'Electricity',
  'Utilities',
  'Life Insurance',
  'Rental Insurance',
  'Daycare',
  'Bank Fees',
  'CC Fees & Interest',
  'Retirement Savings Fund',
  'Child Education Fund',
  'Line of Credit',
  'Education Loan',

  // Flexible
  'Groceries',
  'Restaurants',
  'Food Delivery',
  'Coffee & Snacks',
  'Entertainment',
  'Clothing',
  'Personal Care',
  'Health',
  'Gifts',
  'Gas',
  'Public Transit',
  'Rideshare',
  'Parking',
  'Shopping & Retail',
  'Home & Household',
  'Electronics',
  'Kids & Clothing',

  // Non-Monthly
  'Travel - Airfare',
  'Travel - Lodging',
  'Travel - Other',
  'Medical',
  'Dental',
  'Home Repairs',
  'Auto Repairs',
  'Taxes',
  'Education',

  // Digital Subscriptions
  'Netflix',
  'Disney+',
  'Hulu',
  'Spotify',
  'Apple Music',
  'Amazon Prime',
  'YouTube Premium',
  'Streaming',
  'Music Streaming',
  'Software',

  // Excluded (these will get gray treatment)
  'Transfers',
  'CC Payments',
  'Refunds'
];

/**
 * Non-excluded categories that should NOT render gray
 * (everything except Excluded children and Uncategorized)
 */
export const NON_GRAY_CATEGORIES = ALL_LEAF_CATEGORIES.filter(
  cat => !EXCLUDED_LEAVES.includes(normalizeKey(cat))
);
